import { useTranslation } from 'react-i18next';

import type { AppointmentResponse, DayBoardResponse, DoctorColumnResponse } from '../../api/types';
import { Columns, type Column } from '../../components/charts/Columns';
import { RankedBars, type RankedBar } from '../../components/charts/RankedBars';
import { percent } from '../../components/charts/buckets';
import { count } from '../../i18n/format';
import { clinicTime, minutesIntoDay } from '../scheduling/clinicTime';

/** The four parts a doctor's booked time is drawn in, and what is left: free. */
const PARTS = {
  completed: 'var(--chart-1)',
  here: 'var(--chart-seq-2)',
  later: 'var(--chart-seq-3)',
  noShow: 'var(--chart-2)',
} as const;

const MINUTE = 60_000;

interface DoctorDay {
  readonly key: string;
  readonly name: string;
  readonly available: number;
  readonly completed: number;
  readonly here: number;
  readonly later: number;
  readonly noShow: number;
  readonly away: boolean;
}

/**
 * The day board's charts (CR-35): where the day's time has gone and where it is still free.
 *
 * Everything is worked out here from the board the screen already holds, so the charts move with
 * the board's live updates and cost no request of their own. A doctor's time is their working
 * windows (CR-35: every window, plus extra hours, less time off — the rule the slot search books
 * by), and booked time is every appointment not cancelled, a no-show included: it held the doctor
 * and the room. Free is the difference, which is the number the desk can do something about.
 */
export function DayBoardOverview({
  board,
  isToday,
}: {
  readonly board: DayBoardResponse;
  /** Marks the hour now running in the through-the-day chart. */
  readonly isToday: boolean;
}) {
  const { t } = useTranslation();
  // `5h 30m`, `45m`, `2h`, in the reader's language.
  const duration = (minutes: number) => {
    const whole = Math.round(minutes);
    const h = Math.floor(whole / 60);
    const m = whole % 60;
    if (h === 0) {
      return t('overview.board.minutes', { m });
    }
    return m === 0 ? t('overview.board.hours', { h }) : t('overview.board.hoursMinutes', { h, m });
  };
  const noteFor = (doctor: DoctorDay) => {
    const taken = bookedMinutes(doctor);
    if (doctor.away) {
      return t('overview.board.awayNote');
    }
    if (doctor.available === 0) {
      return t('overview.board.offHours');
    }
    if (taken > doctor.available) {
      return t('overview.board.over', { over: duration(taken - doctor.available) });
    }
    return t('overview.board.freeLeft', { free: duration(doctor.available - taken) });
  };

  const columns = board.columns ?? [];
  const doctors = columns.map((column) => doctorDay(column, t('board.unknownDoctor')));
  const appointments = [...columns.flatMap((column) => column.appointments ?? []), ...(board.unassigned ?? [])];
  const kept = appointments.filter((appointment) => appointment.status !== 'CANCELLED');

  const available = doctors.reduce((sum, doctor) => sum + doctor.available, 0);
  const booked = doctors.reduce((sum, doctor) => sum + bookedMinutes(doctor), 0);
  const free = doctors.reduce((sum, doctor) => sum + Math.max(0, doctor.available - bookedMinutes(doctor)), 0);
  const working = doctors.filter((doctor) => doctor.available > 0).length;
  const away = doctors.filter((doctor) => doctor.away).length;
  const cancelled = appointments.length - kept.length;
  const noShows = kept.filter((appointment) => appointment.status === 'NO_SHOW').length;

  const hours = throughTheDay(columns, kept);
  const nowHour = isToday ? currentClinicHour() : null;
  const hourColumns: Column[] = hours.map((hour) => {
    const bookedHere = Math.min(hour.booked, hour.capacity);
    return {
      key: String(hour.hour),
      label: `${String(hour.hour).padStart(2, '0')}`,
      description: t('overview.board.hourDescription', {
        from: `${String(hour.hour).padStart(2, '0')}:00`,
        to: `${String(hour.hour + 1).padStart(2, '0')}:00`,
        doctors: round(hour.capacity),
        booked: round(hour.booked),
        free: round(Math.max(0, hour.capacity - hour.booked)),
      }),
      segments: [
        { value: bookedHere, colour: 'var(--chart-1)' },
        { value: Math.max(0, hour.capacity - bookedHere), colour: 'var(--chart-track)' },
        // Booked beyond the doctors on duty (outside their hours): drawn above, in the alert hue.
        { value: Math.max(0, hour.booked - hour.capacity), colour: 'var(--chart-2)' },
      ],
    };
  });

  const rooms = roomHours(kept);

  return (
    <div className="overview">
      <section className="measures" aria-label={t('overview.board.strip')}>
        <div className="measure measure--lead">
          <span className="measure__label">{t('overview.board.free')}</span>
          <span className="measure__value">{duration(free)}</span>
          <span className="measure__note">{t('overview.board.freeNote')}</span>
        </div>
        <div className="measure">
          <span className="measure__label">{t('overview.board.booked')}</span>
          <span className="measure__value">{duration(booked)}</span>
          <span className="measure__note">
            {t('overview.board.bookedNote', { available: duration(available), share: percent(booked, available) })}
          </span>
        </div>
        <div className={away > 0 ? 'measure measure--alert' : 'measure'}>
          <span className="measure__label">{t('overview.board.doctors')}</span>
          <span className="measure__value">{count(working)}</span>
          <span className="measure__note">
            {away > 0 ? t('overview.board.away', { count: away }) : t('overview.board.nobodyAway')}
          </span>
        </div>
        <div className="measure">
          <span className="measure__label">{t('overview.board.appointments')}</span>
          <span className="measure__value">{count(kept.length)}</span>
          <span className="measure__note">
            {t('overview.board.appointmentsNote', { cancelled, noShows })}
          </span>
        </div>
      </section>

      <div className="overview-grid">
        <section className="panel">
          <div className="panel__heading">
            <h2>{t('overview.board.perDoctor')}</h2>
            <p className="muted">{t('overview.board.perDoctorNote')}</p>
          </div>
          <ol className="bars bars--plain">
            {doctors.map((doctor) => {
              const taken = bookedMinutes(doctor);
              const span = Math.max(doctor.available, taken);
              const description = t('overview.board.rowDescription', {
                name: doctor.name,
                booked: duration(taken),
                available: duration(doctor.available),
                completed: duration(doctor.completed),
                here: duration(doctor.here),
                later: duration(doctor.later),
                noShow: duration(doctor.noShow),
              });
              return (
                <li key={doctor.key}>
                  <div className="bars__row has-tip" tabIndex={0} aria-label={description}>
                    <span className="bars__label" title={doctor.name}>
                      {doctor.name}
                    </span>
                    <span className="bars__track">
                      <span
                        className="bars__stack"
                        style={{ inlineSize: `${span > 0 ? (taken / span) * 100 : 0}%` }}
                      >
                        {(['completed', 'here', 'later', 'noShow'] as const).map((part) =>
                          doctor[part] > 0 ? (
                            <span key={part} style={{ flexGrow: doctor[part], background: PARTS[part] }} />
                          ) : null,
                        )}
                      </span>
                    </span>
                    <span className="bars__value numeric">
                      {doctor.available > 0
                        ? t('overview.board.doctorValue', { booked: duration(taken), available: duration(doctor.available) })
                        : duration(taken)}
                      <span className="bars__note">{noteFor(doctor)}</span>
                    </span>
                    <span className="chart-tip" role="presentation">
                      {description}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
          <div className="chart__legend">
            {(['completed', 'here', 'later', 'noShow'] as const).map((part) => (
              <span key={part}>
                <span className="chart__key" style={{ background: PARTS[part] }} />
                {t(`overview.board.legend.${part}`)}
              </span>
            ))}
            <span>
              <span className="chart__key" style={{ background: 'var(--chart-track)' }} />
              {t('overview.board.legend.free')}
            </span>
          </div>
        </section>

        {hourColumns.length > 0 ? (
          <section className="panel">
            <div className="panel__heading">
              <h2>{t('overview.board.throughDay')}</h2>
              <p className="muted">{t('overview.board.throughDayNote')}</p>
            </div>
            <Columns
              columns={hourColumns}
              label={t('overview.board.throughDay')}
              formatTick={(value) => String(round(value))}
              current={nowHour === null ? undefined : String(nowHour)}
            />
            <div className="chart__legend">
              <span>
                <span className="chart__key" style={{ background: 'var(--chart-1)' }} />
                {t('overview.board.legend.booked')}
              </span>
              <span>
                <span className="chart__key" style={{ background: 'var(--chart-track)' }} />
                {t('overview.board.legend.free')}
              </span>
              {hours.some((hour) => hour.booked > hour.capacity) ? (
                <span>
                  <span className="chart__key" style={{ background: 'var(--chart-2)' }} />
                  {t('overview.board.legend.outside')}
                </span>
              ) : null}
            </div>
          </section>
        ) : null}

        {rooms.length > 1 ? (
          <section className="panel">
            <div className="panel__heading">
              <h2>{t('overview.board.rooms')}</h2>
              <p className="muted">{t('overview.board.roomsNote')}</p>
            </div>
            <RankedBars rows={rooms} format={duration} />
          </section>
        ) : null}
      </div>
    </div>
  );
}

function bookedMinutes(doctor: DoctorDay): number {
  return doctor.completed + doctor.here + doctor.later + doctor.noShow;
}

function minutesOf(appointment: AppointmentResponse): number {
  const from = Date.parse(appointment.startsAt ?? '');
  const to = Date.parse(appointment.endsAt ?? '');
  return Number.isFinite(from) && Number.isFinite(to) && to > from ? (to - from) / MINUTE : 0;
}

/**
 * A doctor's working minutes, from the day's windows. A backend older than CR-35 sends no windows,
 * so the header's span stands in — the same figure the column has always shown.
 */
function workingMinutes(column: DoctorColumnResponse): number {
  const windows = column.windows ?? [];
  if (windows.length > 0) {
    return windows.reduce((sum, window) => {
      const from = Date.parse(window.from ?? '');
      const until = Date.parse(window.until ?? '');
      return sum + (Number.isFinite(from) && Number.isFinite(until) && until > from ? (until - from) / MINUTE : 0);
    }, 0);
  }
  if (column.windows === undefined && column.workingFrom && column.workingUntil && !column.absent) {
    return Math.max(0, (Date.parse(column.workingUntil) - Date.parse(column.workingFrom)) / MINUTE);
  }
  return 0;
}

function doctorDay(column: DoctorColumnResponse, unknown: string): DoctorDay {
  let completed = 0;
  let here = 0;
  let later = 0;
  let noShow = 0;
  for (const appointment of column.appointments ?? []) {
    const minutes = minutesOf(appointment);
    switch (appointment.status) {
      case 'COMPLETED':
        completed += minutes;
        break;
      case 'ARRIVED':
      case 'IN_PROGRESS':
        here += minutes;
        break;
      case 'NO_SHOW':
        noShow += minutes;
        break;
      case 'CANCELLED':
        break;
      default:
        later += minutes;
    }
  }
  const available = workingMinutes(column);
  return {
    key: column.employmentId ?? column.doctorName ?? unknown,
    name: column.doctorName ?? unknown,
    available,
    completed,
    here,
    later,
    noShow,
    away: Boolean(column.absent) && available === 0,
  };
}

interface HourLoad {
  readonly hour: number;
  /** Doctors on duty in this hour, in hours: two doctors for half of it is 1. */
  readonly capacity: number;
  /** Appointment time in this hour, in hours, cancellations left out. */
  readonly booked: number;
}

/** Each clinic hour from the day's first working or booked minute to its last. */
function throughTheDay(columns: readonly DoctorColumnResponse[], kept: readonly AppointmentResponse[]): HourLoad[] {
  const spans: { from: number; to: number; kind: 'capacity' | 'booked' }[] = [];
  for (const column of columns) {
    for (const window of column.windows ?? []) {
      if (window.from && window.until) {
        spans.push({ from: minutesIntoDay(window.from), to: minutesIntoDay(window.until) || 1440, kind: 'capacity' });
      }
    }
  }
  for (const appointment of kept) {
    if (appointment.startsAt && appointment.endsAt) {
      spans.push({
        from: minutesIntoDay(appointment.startsAt),
        to: minutesIntoDay(appointment.endsAt) || 1440,
        kind: 'booked',
      });
    }
  }
  if (spans.length === 0) {
    return [];
  }
  const first = Math.floor(Math.min(...spans.map((span) => span.from)) / 60);
  const last = Math.ceil(Math.max(...spans.map((span) => span.to)) / 60);
  const hours: HourLoad[] = [];
  for (let hour = first; hour < last; hour++) {
    const start = hour * 60;
    const end = start + 60;
    let capacity = 0;
    let booked = 0;
    for (const span of spans) {
      const overlap = Math.max(0, Math.min(end, span.to) - Math.max(start, span.from)) / 60;
      if (span.kind === 'capacity') {
        capacity += overlap;
      } else {
        booked += overlap;
      }
    }
    hours.push({ hour, capacity, booked });
  }
  return hours;
}

/** Booked time per room, largest first. Appointments with no room are left out. */
function roomHours(kept: readonly AppointmentResponse[]): RankedBar[] {
  const rooms = new Map<string, RankedBar>();
  for (const appointment of kept) {
    if (!appointment.roomId) {
      continue;
    }
    const running = rooms.get(appointment.roomId);
    rooms.set(appointment.roomId, {
      key: appointment.roomId,
      label: appointment.roomName ?? appointment.roomId,
      value: (running?.value ?? 0) + minutesOf(appointment),
    });
  }
  return [...rooms.values()].sort((a, b) => b.value - a.value);
}

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

function currentClinicHour(): number {
  return Number(clinicTime(new Date().toISOString()).slice(0, 2));
}
